import { useRef, useState } from 'react'

import { Box, Button, Form } from '@factorialco/deprecated-design-system'
import { RichTextEditor } from '@factorialco/factorial-one-react/dist/experimental'
import { FormikProvider } from 'formik'
import {
  AtsEvaluationFormAnswer,
  AtsFeedbackDetailFragment,
  EvaluationFormListItemFragment,
} from 'generated/resources/graphql'
import { isEqual } from 'lodash'

import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { FieldArray, useForm } from 'modules/core/hooks/useForm'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AnswerItem } from 'modules/ats/components/EvaluationForm/components/EvaluationFormItem/AnswerItem'
import { useUpdateFeedback } from 'modules/ats/hooks/feedbacks/useUpdateFeedback'
import { calculateAverageRating, parseQuestions } from 'modules/ats/lib/utils/evaluationForms'
import { AtsFeedbackFormValues } from 'modules/ats/lib/utils/feedbacks'
import {
  useNotesMentions,
  useRichTextEditorNotesMentions,
} from 'modules/ats/lib/utils/useNotesMentions'

interface Props {
  feedback: AtsFeedbackDetailFragment
  evaluationForm: EvaluationFormListItemFragment
  onEditFinish: () => void
}

interface AtsEvaluationFormValues extends AtsFeedbackFormValues {
  evaluationFormAnswers: Record<string, AtsEvaluationFormAnswer>
}

export const EvaluationFormEdit = ({ feedback, evaluationForm, onEditFinish }: Props) => {
  const { updateFeedback } = useUpdateFeedback(feedback)
  const [accessesIds, setAccessesIds] = useState<number[] | null>([])
  const editorRef = useRef<EditorHandle>(null)
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()
  const { candidate, applicationPhase, application, description, evaluationFormAnswers, rating } =
    feedback
  const uniqueMentioningAccesses = useNotesMentions({
    atsJobPostingId: application?.jobPosting.id,
    candidateId: candidate.id,
  })
  const {
    accesses: uniqueRichTextEditorMentioningAccesses,
    loading: richTextEditorMentionsLoading,
  } = useRichTextEditorNotesMentions({
    atsJobPostingId: application?.jobPosting.id,
    candidateId: candidate.id,
  })
  const parsedQuestions = parseQuestions(evaluationForm.questions)
  const parsedAnswers = evaluationFormAnswers?.reduce((all, answer) => {
    return { ...all, [answer.questionId]: answer }
  }, {})

  const onFiles = useUnsafeCallback(
    (attachments) => {
      if (isEqual(attachments, form.values.attachments)) {
        return
      }
      form.setFieldValue('attachments', attachments)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEditFinish]
  )

  // TODO: Make files updates & delete work
  const form = useForm<AtsEvaluationFormValues>({
    defaultValues: {
      description,
      rating,
      atsCandidateId: candidate.id,
      atsApplicationId: application?.id,
      atsApplicationPhaseId: applicationPhase?.id,
      attachments: [],
      mentionIds: accessesIds,
      evaluationFormAnswers: parsedAnswers || {},
    },
    async onSubmit(data, { setErrors }) {
      const answers = Object.values(data.evaluationFormAnswers).map((answer) => ({
        questionId: answer.questionId,
        note: answer.note,
        score: answer.score,
      }))

      const result = await updateFeedback({
        ...data,
        atsApplicationPhaseId: applicationPhase?.id,
        evaluationFormAnswers: answers,
        rating: calculateAverageRating(answers.map((answer) => answer.score)),
        attachments: data.attachments && data.attachments.length > 0 ? data.attachments : null,
      })

      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.evaluation_forms.edit.success_message'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('ats.evaluation_forms.edit.error_message'),
        })
      }

      onEditFinish()

      return handled
    },
  })

  return (
    <FormikProvider value={form}>
      <Form onSubmit={() => form.handleSubmit()} padding={null}>
        <Box gap="s8" flexDirection="row" alignItems="center" maxWidth="s600">
          <UserAvatar user={feedback.access || undefined} size="s40" />
        </Box>

        <Box gap="s8">
          <Box
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
            borderRadius={{ all: 'abs008' }}
            background="grey000White"
            gapDivider
          >
            <FieldArray
              name="answers"
              render={() =>
                parsedQuestions.map((question) => (
                  <AnswerItem key={question.id} question={question} />
                ))
              }
            />
          </Box>
        </Box>

        {richTextEditorLoading || richTextEditorMentionsLoading ? (
          <RichTextEditor.Skeleton />
        ) : canUseRichTextEditor ? (
          <RichTextEditor
            {...editorConfig}
            title={evaluationForm?.name}
            onChange={(data) => {
              form.setFieldValue('description', data?.value)
              setAccessesIds(data?.mentionIds || null)
            }}
            placeholder={i18n.t('ats.feedbacks.placeholder')}
            maxCharacters={5000}
            mentionsConfig={{
              users: uniqueRichTextEditorMentioningAccesses,
            }}
            initialEditorState={{ content: form.values.description ?? '' }}
            filesConfig={{
              onFiles: onFiles,
              multipleFiles: true,
            }}
          />
        ) : (
          <TextEditor
            ref={editorRef}
            onChange={(data) => {
              if (data && typeof data !== 'string') {
                form.setFieldValue('description', data.value)
                setAccessesIds(data?.ids)
              }
            }}
            onFiles={onFiles}
            placeholder={i18n.t('ats.feedbacks.placeholder')}
            initialEditorState={{
              data: form.values.description || '',
              files: form.values.attachments || [],
            }}
            height="s200"
            hasMentions
            users={uniqueMentioningAccesses}
            enableBetaFeatures={false}
          />
        )}

        <Box flexDirection="row" justifyContent="flexEnd" gap="s8">
          <Button type="secondary" disabled={form.isSubmitting} onClick={onEditFinish}>
            {i18n.t('common.cancel')}
          </Button>

          <Button disabled={form.isSubmitting || !form.isValid} submit>
            {i18n.t('common.save')}
          </Button>
        </Box>
      </Form>
    </FormikProvider>
  )
}
